import { useCallback, useMemo, useState } from 'react'
import type { Creative, CreativeType, TikTokVideoContent } from '../types'
import { MAX_HASHTAGS } from '../types'
import { useFlushPersistNow, useUpdateCreativeContent } from '../store/draft-creative-context'
import { FieldGroup } from '../components/FieldGroup'
import { fieldLengthState, platformContentViolations } from '../utils/validatePlatformContent'
import { useEditorHighlight } from '../hooks/useEditorHighlight'
import { VideoPickerBody } from './fields/VideoPickerBody'
import { HashtagInput } from './fields/HashtagInput'
import { useTikTokCreatorInfo } from '../hooks/useTikTokCreatorInfo'
import { TikTokPostSettingsFields } from '../components/TikTokPostSettingsFields'
import {
  EMPTY_TIKTOK_POST_SETTINGS,
  isCompleteTikTokPostSettings,
  type TikTokPostSettingsDraft,
} from '../utils/tiktok-post-settings'

interface TikTokVideoEditorProps {
  creative: Creative<CreativeType.TikTokVideo>
}

export function TikTokVideoEditor({ creative }: TikTokVideoEditorProps) {
  const updateContent = useUpdateCreativeContent()
  const flushPersistNow = useFlushPersistNow()
  const { editorRef, highlightedSection, handleEditorBlur, setActiveSection } = useEditorHighlight(
    creative.id
  )
  const content = creative.content

  // TikTok's published limit: 2,200 UTF-16 runes for post_info.title (its docs are
  // explicit about the unit, so emoji count as 2 here and only here). The counter
  // measures the caption PLUS the first five hashtags, space-joined, which is what
  // TikTokPublishService sends.
  const violationByField = useMemo(() => {
    const map = new Map<string, string>()
    for (const v of platformContentViolations({ type: creative.type, content })) {
      if (!map.has(v.contentField)) map.set(v.contentField, v.message)
    }
    return map
  }, [creative.type, content])
  const captionLength = fieldLengthState(creative.type, 'caption', content)

  // TikTok's per-post choices live on the creative so a scheduled or autopilot
  // publish (nobody present to answer the dialog) carries them. The options
  // come from the connected creator's own settings.
  const creatorInfo = useTikTokCreatorInfo(true)
  const [settingsDraft, setSettingsDraft] = useState<TikTokPostSettingsDraft>(() => ({
    ...EMPTY_TIKTOK_POST_SETTINGS,
    ...(content.postSettings ?? {}),
  }))

  const update = useCallback(
    (field: keyof TikTokVideoContent, value: unknown) => {
      updateContent(creative.id, { [field]: value })
    },
    [creative.id, updateContent]
  )

  const handleSettingsChange = useCallback(
    (next: TikTokPostSettingsDraft) => {
      setSettingsDraft(next)
      // Only a complete, valid choice set is worth persisting; the server
      // refuses anything less at publish time anyway.
      if (isCompleteTikTokPostSettings(next)) {
        update('postSettings', next)
        flushPersistNow(creative.id)
      }
    },
    [creative.id, flushPersistNow, update]
  )

  return (
    <div ref={editorRef} className="space-y-4" onBlurCapture={handleEditorBlur}>
      <FieldGroup label="Video" sectionId="video" highlighted={highlightedSection === 'video'}>
        <VideoPickerBody
          creativeId={creative.id}
          creativeType={creative.type}
          platform="tiktok"
          selectedVideoAttachmentId={content.videoAttachmentId}
          selectedVideoUrl={content.videoUrl}
          onSelectVideo={(attachmentId, videoUrl, videoSource) => {
            updateContent(creative.id, { videoAttachmentId: attachmentId, videoUrl, videoSource })
            flushPersistNow(creative.id)
          }}
        />
      </FieldGroup>

      <FieldGroup
        label="Caption"
        count={captionLength ? `${captionLength.count}/${captionLength.max}` : undefined}
        countOver={captionLength?.over}
        error={violationByField.get('caption')}
        sectionId="caption"
        highlighted={highlightedSection === 'caption'}
      >
        <textarea
          value={content.caption}
          onChange={e => update('caption', e.target.value)}
          onFocusCapture={() => setActiveSection(creative.id, 'caption')}
          placeholder="Write your caption..."
          rows={4}
          className="w-full resize-none rounded-lg border border-line-bright bg-bg-3 px-3 py-2 text-sm text-paper transition-colors outline-none placeholder:text-paper-faint focus:border-acid"
        />
      </FieldGroup>

      <FieldGroup
        label={`Hashtags (${content.hashtags.length}/${MAX_HASHTAGS})`}
        count={content.hashtags.length >= MAX_HASHTAGS ? 'Limit reached' : undefined}
        sectionId="hashtags"
        highlighted={highlightedSection === 'hashtags'}
      >
        <HashtagInput
          hashtags={content.hashtags}
          onChange={hashtags => update('hashtags', hashtags)}
          maxHashtags={MAX_HASHTAGS}
          onFocusCapture={() => setActiveSection(creative.id, 'hashtags')}
        />
      </FieldGroup>

      <FieldGroup
        label="TikTok post settings"
        error={
          !creatorInfo.loading && !creatorInfo.error && !content.postSettings
            ? 'Choose who can view this video before it can be scheduled or posted.'
            : undefined
        }
        sectionId="postSettings"
        highlighted={highlightedSection === 'postSettings'}
      >
        <TikTokPostSettingsFields
          draft={settingsDraft}
          onChange={handleSettingsChange}
          creatorInfo={creatorInfo.info}
          creatorInfoLoading={creatorInfo.loading}
          creatorInfoError={creatorInfo.error}
          aiGenerated={content.videoSource !== 'uploaded'}
          compact
        />
      </FieldGroup>
    </div>
  )
}
